import { JarvisExecutionPanel } from '../components/jarvis/JarvisExecutionPanel';
import { useJarvisMirrorHistory } from '../hooks/useJarvisMirrorHistory';
import React, {
  useEffect,
  useRef,
  useState
} from 'react';

import {
  Bot,
  Send,
  Loader2,
  MessageSquareText,
  ShieldCheck,
  AlertCircle,
  Copy,
  Check,
  Sparkles
} from 'lucide-react';

import {
  AssistantMessage,
  ChatGPTConversationIdentity,
  DetectedScript,
  ScriptCandidate
} from '@kaizen/shared';

import {
  useRealtime
} from '../contexts/RealtimeContext';

import {
  useProjects
} from '../contexts/ProjectContext';

import {
  ScriptCard
} from '../components/common/ScriptCard';

import {
  RunScriptModal
} from '../components/modals/RunScriptModal';


function shortConversationId(
  chat:
    ChatGPTConversationIdentity |
    null
): string | null {

  if (
    !chat?.conversationId
  ) {
    return null;
  }


  if (
    chat.conversationId.length <=
      16
  ) {

    return chat.conversationId;
  }


  return (
    `${chat.conversationId.slice(0, 8)}…${chat.conversationId.slice(-6)}`
  );
}


function createScriptCandidate(
  script:
    DetectedScript,

  timestamp:
    string
): ScriptCandidate {

  const multiline =
    script.code.includes(
      '\n'
    );


  const isPowerShell =
    script.language ===
      'powershell';


  return {

    id:
      script.id,

    filename:
      script.title ||
      (
        isPowerShell
          ? 'JARVIS_SCRIPT.ps1'
          : 'JARVIS_CODE.txt'
      ),

    type:
      isPowerShell
        ? (
            multiline
              ? 'powershell_script'
              : 'powershell_command'
          )
        : 'code_reference',

    size:
      new Blob(
        [
          script.code
        ]
      ).size,

    source:
      'JARVIS / ChatGPT',

    receivedTime:
      timestamp,

    status:
      'pending',

    command:
      isPowerShell &&
      !multiline
        ? script.code
        : '',

    scriptContent:
      multiline ||
      !isPowerShell
        ? script.code
        : undefined
  };
}


export const AssistantPage:
React.FC = () => {

  const {
    services,
    sendJob,
    device,
    sendJarvisMessage,
    jarvisChat,
    jarvisBusy,
    jarvisTransientState
  } =
    useRealtime();


  const {
    activeProject
  } =
    useProjects();


  const [
    inputMessage,
    setInputMessage
  ] =
    useState(
      ''
    );


  const [
    messages,
    setMessages
  ] =
    useState<AssistantMessage[]>(
      []
    );


  const [
    selectedScript,
    setSelectedScript
  ] =
    useState<ScriptCandidate | null>(
      null
    );


  const [
    isModalOpen,
    setIsModalOpen
  ] =
    useState(
      false
    );


  const [
    isSending,
    setIsSending
  ] =
    useState(
      false
    );


  const [
    requestError,
    setRequestError
  ] =
    useState<string | null>(
      null
    );


  const [
    sendingTo,
    setSendingTo
  ] =
    useState<ChatGPTConversationIdentity | null>(
      null
    );


  const [
    copiedChatId,
    setCopiedChatId
  ] =
    useState(
      false
    );


  const streamRef =
    useRef<HTMLDivElement>(
      null
    );


  const isBridgeConnected =
    services.chatgptBridge ===
      'connected';


  // JARVIS R9 SMART SCROLL
  const shouldAutoScrollRef =
    useRef(
      true
    );

  const [
    showLatest,
    setShowLatest
  ] =
    useState(
      false
    );


  useEffect(
    () => {

      const element =
        streamRef.current;


      if (
        !element
      ) {
        return;
      }


      if (
        shouldAutoScrollRef.current
      ) {

        window.requestAnimationFrame(
          () => {

            element.scrollTop =
              element.scrollHeight;

            setShowLatest(
              false
            );
          }
        );

      }
      else {

        setShowLatest(
          true
        );
      }

    },
    [
      messages,
      isSending
    ]
  );


  const handleStreamScroll =
    () => {

      const element =
        streamRef.current;


      if (
        !element
      ) {
        return;
      }


      const distanceFromBottom =
        element.scrollHeight -
        element.scrollTop -
        element.clientHeight;


      const nearBottom =
        distanceFromBottom <
        110;


      shouldAutoScrollRef.current =
        nearBottom;


      if (
        nearBottom
      ) {

        setShowLatest(
          false
        );
      }
    };


  const scrollToLatest =
    () => {

      const element =
        streamRef.current;


      if (
        !element
      ) {
        return;
      }


      shouldAutoScrollRef.current =
        true;


      element.scrollTo(
        {
          top:
            element.scrollHeight,

          behavior:
            'smooth'
        }
      );


      setShowLatest(
        false
      );
    };


  const handleSendMessage =
    async (
      event:
        React.FormEvent
    ) => {

      event.preventDefault();


      const message =
        inputMessage.trim();


      if (
        !message ||
        isSending ||
        !isBridgeConnected
      ) {

        return;
      }


      const timestamp =
        new Date().toISOString();


      const userMessage:
        AssistantMessage = {

        id:
          `user-${crypto.randomUUID()}`,

        role:
          'user',

        content:
          message,

        timestamp
      };


      setSendingTo(
        jarvisChat
          ? {
              ...jarvisChat
            }
          : null
      );


      setMessages(
        previous => [
          ...previous,
          userMessage
        ]
      );


      setInputMessage(
        ''
      );


      setRequestError(
        null
      );


      setIsSending(
        true
      );


      try {

        const response =
          await sendJarvisMessage(
            message,
            {
              projectId:
                activeProject?.id,

              workingDirectory:
                activeProject?.localPath
            }
          );


        if (
          !response.success
        ) {

          throw new Error(
            response.error ||
            'JARVIS request failed'
          );
        }


        const assistantMessage:
          AssistantMessage = {

          id:
            `jarvis-${response.requestId}`,

          role:
            'assistant',

          content:
            response.message ||
            '(No response text returned)',

          detectedScripts:
            response.detectedScripts,

          timestamp:
            response.completedAt ||
            new Date().toISOString()
        };


        setMessages(
          previous => [
            ...previous,
            assistantMessage
          ]
        );

      }
      catch (
        error:
          unknown
      ) {

        const message =
          error instanceof Error
            ? error.message
            : String(
                error
              );


        setRequestError(
          message
        );


        setMessages(
          previous => [
            ...previous,

            {
              id:
                `error-${crypto.randomUUID()}`,

              role:
                'system',

              content:
                `JARVIS request failed: ${message}`,

              timestamp:
                new Date().toISOString()
            }
          ]
        );

      }
      finally {

        setIsSending(
          false
        );


        setSendingTo(
          null
        );
      }
    };


  const handleRunScriptFromCard =
    (
      candidate:
        ScriptCandidate
    ) => {

      if (
        candidate.type !==
          'powershell_script' &&
        candidate.type !==
          'powershell_command'
      ) {

        return;
      }


      setSelectedScript(
        candidate
      );


      setIsModalOpen(
        true
      );
    };




  // JARVIS V5 HISTORY BINDING
  useJarvisMirrorHistory(
    jarvisChat,
    setMessages
  );

  const conversationId =
    shortConversationId(
      jarvisChat
    );


  return (

    <div
      className="
        min-w-0
        h-[calc(100dvh-8.4rem)]
        md:h-[calc(100vh-7.7rem)]
        md:min-h-[560px]
      "
    >

      <section
        className="
          relative
          h-full
          min-h-0
          overflow-hidden
          flex flex-col
          rounded-[18px]
          border border-white/10
          bg-slate-900/82
          shadow-[0_28px_90px_rgba(2,6,23,0.44)]
          backdrop-blur-2xl
        "
      >

        <div
          aria-hidden="true"
          className="
            pointer-events-none
            absolute
            -top-28 left-[20%]
            w-72 h-72
            rounded-full
            bg-indigo-500/10
            blur-3xl
          "
        />


        {/* HEADER */}

        <div
          className="
            relative
            shrink-0
            flex items-center justify-between
            gap-3
            px-3 sm:px-4
            py-3
            border-b border-white/[0.07]
            bg-white/[0.025]
          "
        >

          <div className="flex items-center gap-3 min-w-0">

            <div
              className="
                relative
                w-10 h-10
                shrink-0
                flex items-center justify-center
                rounded-xl
                border border-indigo-400/20
                bg-indigo-400/10
                text-indigo-300
                shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]
              "
            >

              <Bot className="w-[19px] h-[19px]" />


              {isBridgeConnected && (

                <span
                  className="
                    absolute
                    -right-0.5 -bottom-0.5
                    w-2.5 h-2.5
                    rounded-full
                    bg-emerald-400
                    border-2 border-slate-900
                  "
                />

              )}

            </div>


            <div className="min-w-0">

              <div className="flex items-center gap-2">

                <h2 className="text-[15px] sm:text-base font-semibold text-slate-100 tracking-tight">
                  JARVIS
                </h2>


                <span
                  className={`
                    inline-flex
                    px-2 py-0.5
                    rounded-md
                    border
                    text-[9px]
                    font-medium
                    ${
                      isBridgeConnected
                        ? 'border-emerald-400/15 bg-emerald-400/[0.07] text-emerald-300'
                        : 'border-amber-400/15 bg-amber-400/[0.07] text-amber-300'
                    }
                  `}
                >
                  {isBridgeConnected
                    ? 'CONNECTED'
                    : 'OFFLINE'}
                </span>

              </div>


              <p className="text-[10px] sm:text-[11px] text-slate-500 mt-0.5 truncate">
                Remote AI bridge through your development host
              </p>

            </div>

          </div>


          <Sparkles className="w-4 h-4 text-indigo-400/70 shrink-0" />

        </div>


        {/* CHATGPT TARGET */}

        <div className="relative shrink-0 px-4 sm:px-5 pt-4">

          <div
            className={`
              overflow-hidden
              rounded-xl
              border
              ${
                isBridgeConnected
                  ? 'border-indigo-400/15 bg-indigo-400/[0.055]'
                  : 'border-white/[0.07] bg-white/[0.025]'
              }
            `}
          >

            <div
              className="
                flex items-center gap-3
                px-3.5 py-3
              "
            >

              <MessageSquareText
                className={`w-4 h-4 shrink-0 ${
                  isBridgeConnected
                    ? 'text-indigo-400'
                    : 'text-slate-600'
                }`}
              />


              <div className="min-w-0 flex-1">

                <div className="text-[9px] uppercase tracking-[0.14em] font-semibold text-slate-600">
                  Connected ChatGPT
                </div>

                <div className="truncate text-[11px] sm:text-xs font-medium text-slate-200 mt-0.5">
                  {jarvisChat?.title ||
                    (
                      isBridgeConnected
                        ? 'Detecting conversation…'
                        : 'No active ChatGPT conversation'
                    )}
                </div>

              </div>


              {conversationId && (

                <button
                  type="button"
                  onClick={() => {

                    if (
                      !jarvisChat?.conversationId
                    ) {

                      return;
                    }


                    void navigator.clipboard.writeText(
                      jarvisChat.conversationId
                    );


                    setCopiedChatId(
                      true
                    );


                    setTimeout(
                      () =>
                        setCopiedChatId(
                          false
                        ),
                      1500
                    );
                  }}
                  className="
                    shrink-0
                    h-8
                    px-2
                    flex items-center gap-1.5
                    rounded-md
                    border border-white/[0.06]
                    bg-white/[0.025]
                    text-[9px]
                    font-mono
                    text-slate-500
                  "
                  title="Copy full conversation ID"
                >

                  <span className="hidden sm:inline">
                    {conversationId}
                  </span>

                  {copiedChatId
                    ? <Check className="w-3 h-3 text-emerald-400" />
                    : <Copy className="w-3 h-3" />}

                </button>

              )}

            </div>


            {activeProject && (

              <div
                className="
                  px-3.5 py-2
                  border-t border-white/[0.06]
                  text-[9px]
                  text-slate-600
                  truncate
                "
              >

                Project context:{' '}

                <span className="text-slate-400">
                  {activeProject.name}
                </span>

              </div>

            )}

          </div>

        </div>


        {/* CONVERSATION */}

        <div
          ref={streamRef}
          onScroll={handleStreamScroll}
          className="
            relative
            flex-1
            min-h-0
            overflow-y-auto
            overflow-x-hidden
            px-3 sm:px-4
            py-3
            space-y-3
          "
        >

          {messages.length === 0 && (

            <div
              className="
                min-h-full
                flex flex-col
                items-center justify-center
                text-center
                px-6
              "
            >

              <div
                className="
                  w-12 h-12
                  rounded-xl
                  border border-indigo-400/15
                  bg-indigo-400/[0.06]
                  flex items-center justify-center
                  text-indigo-300
                  shadow-[inset_0_1px_0_rgba(255,255,255,0.025)]
                "
              >

                <Bot className="w-5 h-5" />

              </div>


              <h3 className="mt-3 text-sm font-semibold text-slate-200">
                JARVIS is ready
              </h3>

              <p className="mt-1 max-w-sm text-[10px] sm:text-xs text-slate-600">
                Your messages use the connected ChatGPT conversation shown above.
              </p>

            </div>

          )}


          {messages.map(
            msg => (

              <div
                key={
                  msg.id
                }
                className={`flex flex-col ${
                  msg.role === 'user'
                    ? 'items-end'
                    : 'items-start'
                }`}
              >

                <div className="flex items-center gap-2 mb-1 px-0.5">

                  <span className="text-[9px] font-semibold uppercase tracking-[0.08em] text-slate-600">

                    {msg.role === 'user'
                      ? 'You'
                      : msg.role === 'system'
                        ? 'System'
                        : 'JARVIS'}

                  </span>


                  <span className="text-[9px] font-mono text-slate-700">

                    {new Date(
                      msg.timestamp
                    ).toLocaleTimeString(
                      [],
                      {
                        hour:
                          '2-digit',

                        minute:
                          '2-digit'
                      }
                    )}

                  </span>

                </div>


                <div
                  className={`${
                    msg.role === 'user'
                      ? 'max-w-[88%] sm:max-w-[72%]'
                      : 'w-full max-w-full'
                  } min-w-0 overflow-hidden rounded-xl border px-3 sm:px-3.5 py-2.5 text-[13px] sm:text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? `
                          border-indigo-400/20
                          bg-indigo-500/80
                          text-white
                          shadow-[0_10px_30px_rgba(79,70,229,0.14)]
                        `
                      : msg.role === 'system'
                        ? `
                            border-rose-400/15
                            bg-rose-400/[0.07]
                            text-rose-200
                          `
                        : `
                            border-white/[0.07]
                            bg-white/[0.03]
                            text-slate-200
                            shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]
                          `
                  }`}
                >

                  <p className="whitespace-pre-wrap break-words">
                    {msg.content}
                  </p>


                  {msg.detectedScripts &&
                   msg.detectedScripts.length > 0 && (

                    <div className="mt-2.5 pt-2.5 border-t border-white/[0.06] space-y-2.5">

                      {msg.detectedScripts.map(
                        script => {

                          const candidate =
                            createScriptCandidate(
                              script,
                              msg.timestamp
                            );


                          if (
                            script.language ===
                              'powershell'
                          ) {

                            return (

                              <div
                                key={
                                  script.id
                                }
                              >

                                <div className="text-[9px] uppercase tracking-[0.12em] font-semibold text-slate-600 mb-1.5">
                                  PowerShell candidate — approval required
                                </div>

                                <ScriptCard
                                  script={
                                    candidate
                                  }
                                  onRun={
                                    handleRunScriptFromCard
                                  }
                                  onView={
                                    handleRunScriptFromCard
                                  }
                                />

                              </div>
                            );
                          }


                          return (

                            <div
                              key={
                                script.id
                              }
                              className="
                                overflow-hidden
                                rounded-xl
                                border border-white/[0.07]
                                bg-[#020817]
                              "
                            >

                              <div
                                className="
                                  flex items-center justify-between
                                  px-3 py-2
                                  border-b border-slate-800
                                  bg-slate-900/75
                                "
                              >

                                <span className="text-[9px] uppercase font-semibold tracking-[0.12em] text-slate-600">
                                  {script.language}
                                </span>


                                <button
                                  type="button"
                                  onClick={() =>
                                    void navigator.clipboard.writeText(
                                      script.code
                                    )
                                  }
                                  className="text-slate-500 hover:text-slate-200"
                                >

                                  <Copy className="w-3.5 h-3.5" />

                                </button>

                              </div>


                              <pre className="p-3 overflow-x-auto text-[11px] leading-relaxed text-slate-200 whitespace-pre-wrap break-words">
                                {script.code}
                              </pre>

                            </div>
                          );
                        }
                      )}

                    </div>

                  )}

                </div>

              </div>
            )
          )}


          {(isSending || jarvisBusy || jarvisTransientState !== 'idle') && (

            <div className="flex flex-col items-start">

              <div className="text-[9px] uppercase tracking-[0.08em] font-semibold text-slate-600 mb-1.5 px-1">
                JARVIS
              </div>


              <div
                className="
                  inline-flex items-center gap-2
                  px-3.5 py-3
                  rounded-xl
                  border border-indigo-400/15
                  bg-indigo-400/[0.055]
                  text-xs text-slate-300
                "
              >

                <Loader2 className="w-3.5 h-3.5 text-indigo-400 animate-spin" />


                <div>

                  <div className="font-medium">
                    {jarvisTransientState === 'generating'
                      ? 'JARVIS is generating…'
                      : jarvisTransientState === 'thinking'
                        ? 'JARVIS is thinking…'
                        : 'JARVIS is working…'}
                  </div>


                  {sendingTo?.title && (

                    <div className="text-[9px] text-slate-600 mt-0.5">
                      Pinned to: {sendingTo.title}
                    </div>

                  )}

                </div>

              </div>

            </div>

          )}


          {showLatest && (

            <button
              type="button"
              onClick={scrollToLatest}
              className="
                sticky
                bottom-2
                z-30
                ml-auto
                flex
                w-fit
                items-center
                gap-1.5
                rounded-full
                border border-indigo-400/20
                bg-slate-900/90
                px-3 py-1.5
                text-[10px]
                font-medium
                text-indigo-200
                shadow-[0_10px_35px_rgba(2,6,23,0.55)]
                backdrop-blur-xl
              "
            >
              ↓ Latest
            </button>
          )}

        </div>



        {/* JARVIS V5 EXECUTION PANEL */}
        <JarvisExecutionPanel />

        {/* ERROR */}

        {requestError && (

          <div className="shrink-0 px-4 sm:px-5 pb-3">

            <div
              className="
                flex items-start gap-2
                px-3 py-2.5
                rounded-xl
                border border-rose-400/15
                bg-rose-400/[0.07]
                text-[10px]
                text-rose-300
              "
            >

              <AlertCircle className="w-4 h-4 shrink-0" />

              <span>
                {requestError}
              </span>

            </div>

          </div>

        )}


        {/* COMPOSER */}

        <div
          className="
            relative
            shrink-0
            px-4 sm:px-5
            py-3.5
            border-t border-white/[0.07]
            bg-slate-900/75
            backdrop-blur-xl
          "
        >

          <form
            onSubmit={
              handleSendMessage
            }
            className="space-y-2"
          >

            <div
              className="
                relative
                rounded-xl
                border border-slate-700/80
                bg-[#020817]
                focus-within:border-indigo-400/60
                focus-within:ring-1
                focus-within:ring-indigo-500/25
                shadow-[inset_0_1px_0_rgba(255,255,255,0.025)]
              "
            >

              <textarea
                value={
                  inputMessage
                }
                onChange={
                  event =>
                    setInputMessage(
                      event.target.value
                    )
                }
                rows={
                  2
                }
                disabled={
                  !isBridgeConnected ||
                  isSending ||
                  jarvisBusy
                }
                placeholder={
                  isBridgeConnected
                    ? 'Message JARVIS…'
                    : 'JARVIS bridge is offline'
                }
                className="
                  block
                  w-full
                  min-h-[68px]
                  resize-none
                  bg-transparent
                  px-3
                  pt-3
                  pb-10
                  pr-12
                  text-sm
                  text-slate-100
                  placeholder:text-slate-700
                  focus:outline-none
                  disabled:opacity-50
                "
              />


              <button
                type="submit"
                disabled={
                  !inputMessage.trim() ||
                  !isBridgeConnected ||
                  isSending ||
                  jarvisBusy
                }
                className="
                  absolute
                  right-2
                  bottom-2
                  w-8 h-8
                  rounded-lg
                  border border-indigo-400/25
                  bg-indigo-500/90
                  hover:bg-indigo-500
                  disabled:bg-slate-800
                  disabled:border-slate-700
                  disabled:text-slate-600
                  text-white
                  flex items-center justify-center
                  shadow-[0_8px_24px_rgba(79,70,229,0.18)]
                "
              >

                {isSending
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Send className="w-3.5 h-3.5" />}

              </button>


              <div className="absolute left-3 bottom-2 flex items-center gap-1.5 text-[9px] text-slate-600">

                <ShieldCheck className="w-3 h-3 text-emerald-400" />

                <span>
                  Scripts require approval
                </span>

              </div>

            </div>

          </form>

        </div>

      </section>


      <RunScriptModal
        script={
          selectedScript
        }
        workingDirectory={
          activeProject?.localPath ||
          'F:\\KAIZEN REMOTE'
        }
        projectName={
          activeProject?.name
        }
        isOpen={
          isModalOpen
        }
        onClose={() =>
          setIsModalOpen(
            false
          )
        }
        onConfirm={
          async script => {

            setIsModalOpen(
              false
            );


            const isLongScript =
              script.type ===
                'powershell_script';


            await sendJob({

              deviceId:
                device?.id ||
                '',

              projectId:
                activeProject?.id,

              type:
                isLongScript
                  ? 'powershell_script'
                  : 'powershell_command',

              payload:
                isLongScript
                  ? {
                      scriptContent:
                        script.scriptContent ||
                        script.command
                    }
                  : {
                      command:
                        script.command ||
                        script.scriptContent ||
                        ''
                    },

              workingDirectory:
                script.workingDirectory ||
                activeProject?.localPath ||
                'F:\\KAIZEN REMOTE'
            });
          }
        }
      />

    </div>
  );
};